import React, { useEffect, useState, Suspense, lazy } from "react";
import { Helmet } from "react-helmet";
import ProjectCardRow from "../../components/ProjectCardRow/ProjectCardRow";
import { getStoredData } from "../../JsonFiles/fetchData";
import LoadingIcon from "../../components/LoadingIcon/LoadingIcon";

// Lazy load components
const AllIntroTemplateLazy = lazy(() =>
  import("../../components/AllIntroTemplate/AllIntroTemplate")
);

function CompletedProjectsPage() {
  const [totalData, setTotalData] = useState(null); // State for TotalData
  const [completedProjects, setCompletedProjects] = useState([]); // State for completed projects
  const [isLoading, setIsLoading] = useState(true); // Loading state

  const structuredData = {
    "@context": "http://schema.org",
    "@type": "WebPage",
    name: "Completed Projects - Priya Infra",
    url: "https://www.priyainfra.com/projects/completed",
    description:
      "Explore our completed construction projects that demonstrate our excellence and quality across diverse infrastructure sectors.",
    publisher: {
      "@type": "Organization",
      name: "Priya Infra",
      url: "https://www.priyainfra.com",
    },
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch and set TotalData from local storage
        const storedData = getStoredData();
        if (storedData) {
          setTotalData(storedData);
        }

        // Fetch projects from backend API
        const projectsResponse = await fetch(
          "https://technow-overseasbackend.vercel.app/projects"
        );
        const projectsData = await projectsResponse.json();

        // ✅ Filter only completed projects
        // ✅ Include all projects that are NOT Completed
        const filtered = projectsData.filter(
          (proj) => !proj.status || proj.status.toLowerCase() !== "ongoing"
        );

        setCompletedProjects(filtered);
      } catch (error) {
        console.error("Error fetching completed projects:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  if (isLoading) {
    return <LoadingIcon />; // ✅ Show loading while fetching
  }

  if (!totalData || completedProjects.length === 0) {
    return (
      <div className="CompletedProjectsPage-empty">
        No completed projects found.
      </div>
    );
  }

  return (
    <div className="CompletedProjectsPage-container">
      <Helmet>
        <title>Completed Projects - Priya Infra</title>
        <meta
          name="description"
          content="Explore our successfully completed infrastructure projects delivered with precision, safety, and engineering excellence."
        />
        <meta property="og:title" content="Completed Projects - Priya Infra" />
        <meta
          property="og:description"
          content="Discover completed projects by Priya Infra showcasing our proven expertise and quality execution."
        />
        <meta
          property="og:url"
          content="https://www.priyainfra.com/projects/completed"
        />
        <meta
          property="og:image"
          content="https://www.priyainfra.com/assets/og_completed_projects.jpg"
        />{" "}
        {/* Replace with actual OG image */}
        <meta property="og:type" content="website" />
        <link
          rel="canonical"
          href="https://www.priyainfra.com/projects/completed"
        />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <Suspense fallback={<div>Loading...</div>}>
        <AllIntroTemplateLazy
          description={totalData[0]?.CompletedProjectsDescription}
          image={totalData[0]?.CompletedProjectsImage}
          title={totalData[0]?.CompletedProjectsTitle}
        />
        <ProjectCardRow data={completedProjects} />
      </Suspense>
    </div>
  );
}

export default CompletedProjectsPage;
